import { useEffect } from 'react';

/**
 * Editor keyboard shortcuts.
 *
 * Bound on the document rather than on the timeline, because the gesture that
 * follows a click on the *stage* is usually a keyboard one — requiring the
 * timeline to hold focus would mean clicking back into it before every cut.
 *
 * The cost of that reach is that typing anywhere on the page would otherwise
 * trigger edits, so text entry is checked for first and always wins.
 */

const isTypingTarget = (target) => {
    if (!target) return false;
    const tag = String(target.tagName || '').toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
};

export function useTimelineKeys({
    enabled,
    onPlayPause,
    onStep,
    onSplit,
    onDelete,
    onUndo,
    onRedo,
    onTrimToPlayhead,
}) {
    useEffect(() => {
        if (!enabled) return undefined;

        const handler = (event) => {
            if (isTypingTarget(event.target)) return;

            const accel = event.metaKey || event.ctrlKey;
            if (accel && event.key.toLowerCase() === 'z') {
                event.preventDefault();
                (event.shiftKey ? onRedo : onUndo)();
                return;
            }
            if (accel && event.key.toLowerCase() === 'y') {
                event.preventDefault();
                onRedo();
                return;
            }
            if (accel) return;

            switch (event.key) {
                case ' ':
                    event.preventDefault();
                    onPlayPause();
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    onStep(-1);
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    onStep(1);
                    break;
                case 's':
                case 'S':
                    event.preventDefault();
                    onSplit();
                    break;
                case 'Delete':
                case 'Backspace':
                    event.preventDefault();
                    onDelete();
                    break;
                case '[':
                    event.preventDefault();
                    onTrimToPlayhead('left');
                    break;
                case ']':
                    event.preventDefault();
                    onTrimToPlayhead('right');
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [enabled, onPlayPause, onStep, onSplit, onDelete, onUndo, onRedo, onTrimToPlayhead]);
}
